import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse } from '../../types/api';
import { API_CONFIG } from '../../config/api.config';

// Classe base com configurações comuns do Axios
export abstract class BaseApiService {
  protected api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  // Configura interceptors comuns
  private setupInterceptors(): void {
    // Interceptor para adicionar token de autenticação
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // FormData exige multipart com boundary definido pelo browser.
        // O default application/json (ou multipart sem boundary) quebra o bind do IFormFile.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          const headers = config.headers as { delete?: (name: string) => void; ['Content-Type']?: unknown };
          if (typeof headers?.delete === 'function') {
            headers.delete('Content-Type');
          } else if (headers) {
            delete headers['Content-Type'];
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para tratar erros de resposta
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403 &&
            error.response?.data?.code === 'ACCESS_RESTRICTED') {
          window.location.href = '/access-restricted';
          return Promise.reject(error);
        }
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Método auxiliar para extrair dados da resposta
  protected extractData<T>(response: AxiosResponse<ApiResponse<T> | T>): T {
    // Verifica se é uma resposta estruturada com success/data
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro na requisição');
      }
      return response.data.data!;
    }

    // Se não é uma resposta estruturada, retorna os dados diretamente
    return response.data as T;
  }

  // Métodos públicos para gerenciar headers de autorização
  public setAuthToken(token: string): void {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  public removeAuthToken(): void {
    delete this.api.defaults.headers.common['Authorization'];
  }
}
